import {ListItem} from '@material-ui/core';
import {NxSelectOption} from '@nextbank/ui-components';
import {useField, useFormikContext} from 'formik';
import {isNil} from 'lodash';
import isString from 'lodash/isString';
import React, {ReactElement, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {DictionaryEntry} from '../../../../../../shared/model/dictionary.model';
import {getGeolocationByParentIdAndName} from '../../../../../../utils/geolocation-helper';
import {SelectHelper} from '../../../../../../utils/select-helper';
import {LosAutocomplete} from '../../../../../shared/autocomplete/LosAutocomplete';
import {NxFormikInput} from '@nextbank/ui-components';
import {FieldDisablementReason} from '../../shared/configured-fields/field-disablement-reason.model';
import {Address} from './address.model';

export interface AddressFormEntries {
  province?: DictionaryEntry[],
  city?: DictionaryEntry[],
  barangay?: DictionaryEntry[],
}

interface Props {
  /**
   * parentFieldPath: string representation of path in object for eg.: borrower.addresses[1].number.
   */
  formPath: string;
  disablementReason?: FieldDisablementReason;
  disabled?: boolean;
  entries: AddressFormEntries;
}

const TransPath = 'LOAN_APPLICATIONS.CUSTOMER_DATA.ADDRESSES';

export default function GeolocationForm({entries, formPath, disabled, disablementReason}: Props): ReactElement {
  const {t} = useTranslation();
  const context = useFormikContext();

  const getName = (key: keyof Address): string => `${formPath}.${key}`;

  const [provinceIdField, provinceIdMeta, provinceIdHelpers] = useField(getName('provinceId'));
  const [provinceField, , provinceHelpers] = useField(getName('province'));
  const [cityField, cityMeta, cityHelpers] = useField(getName('city'));
  const [barangayField, barangayMeta, barangayHelpers] = useField(getName('barangay'));
  const isProvinceEmpty = isNil(provinceIdField.value) && isNil(provinceField.value);
  const isCityEmpty = isNil(cityField.value);

  const provinceIdOnChange = (value: string | NxSelectOption<number> | null): void => {
    cityHelpers.setValue(null, context.validateOnChange);
    barangayHelpers.setValue(null, context.validateOnChange);

    if (isString(value) || isNil(value)) {
      provinceIdHelpers.setValue(null, context.validateOnChange);
      provinceHelpers.setValue(null, context.validateOnChange);
      return;
    }

    // When selecting an option, set both id and label for compatibility with validators/mappers
    provinceIdHelpers.setValue(value?.value ?? null, context.validateOnChange);
    provinceHelpers.setValue(value?.label ?? null, context.validateOnChange);
  };
  const cityOnChange = (value: string | null): void => {
    barangayHelpers.setValue(null, context.validateOnChange);
    cityHelpers.setValue(value, context.validateOnChange);
  };
  const barangayOnChange = (value: string | null): void => barangayHelpers.setValue(value, context.validateOnChange);

  const provinceOptions = SelectHelper.mapToNumberOptions(entries.province);

  const effectiveProvinceId = provinceIdField.value ?? null;

  const filteredCities = useMemo(() => entries.city?.filter((entry) => entry.parentId === effectiveProvinceId),
    [effectiveProvinceId]
  );

  const cityLabels = useMemo(() => SelectHelper.toLabelArray(filteredCities), [effectiveProvinceId]);

  const barangayLabels = useMemo(() => {
    const city = getGeolocationByParentIdAndName(filteredCities, effectiveProvinceId, cityField.value);
    const filteredBarangays = entries.barangay?.filter(entry => entry.parentId === city?.id);
    return SelectHelper.toLabelArray(filteredBarangays);
  }, [cityField.value]);

  const provinceValue = useMemo(
    () => provinceOptions.find((value) => value.value === provinceIdField.value),
    [provinceIdField.value, provinceOptions]
  );

  return (
    <>
      {
        isNil(provinceIdField.value) && !isNil(provinceField.value)
          ? (
            <NxFormikInput
              label={t(TransPath + '.PROVINCE')}
              name={getName('province')}
              disabled={disabled}
              required
            />
          )
          : (
            <LosAutocomplete<NxSelectOption<number>>
              label={t(TransPath + '.PROVINCE')}
              value={provinceValue ?? null}  // ensure never undefined
              options={provinceOptions}
              disabled={disabled}
              onChange={(newValue) => provinceIdOnChange(newValue)}
              renderOption={option => option.label ?? ''}
              getOptionLabel={option => option.label as string ?? ''}
              disablementReason={disablementReason}
              error={provinceIdMeta.error}
              required
            />
          )
      }
      <LosAutocomplete<string>
        label={t(TransPath + '.CITY')}
        value={cityField.value ?? null}  // ensure never undefined
        options={cityLabels ?? []}
        disabled={disabled || isProvinceEmpty}
        onChange={(newValue) => cityOnChange(newValue)}
        hint={t(TransPath + '.TYPE_FOR_MORE_RESULTS')}
        disablementReason={isProvinceEmpty
          ? FieldDisablementReason.PROVINCE_FIELD_MUST_BE_FILLED_FIRST
          : disablementReason}
        error={cityMeta.error}
        required
      />
      <LosAutocomplete<string>
        label={t(TransPath + '.BARANGAY')}
        value={barangayField.value ?? null}  // ensure never undefined
        options={barangayLabels ?? []}
        disabled={disabled || isCityEmpty}
        onChange={(newValue) => barangayOnChange(newValue)}
        hint={t(TransPath + '.TYPE_FOR_MORE_RESULTS')}
        disablementReason={isCityEmpty ? FieldDisablementReason.CITY_FIELD_MUST_BE_FILLED_FIRST : disablementReason}
        error={barangayMeta.error}
        required
      />
    </>
  );
}

